"use client";

import type { SessionUser } from "@/lib/session";
import {
  getFullDisplayName,
  getUserInitials,
  sessionUserToProfile,
  type UserProfileDisplay,
} from "@/lib/userProfileDisplay";
import { authDebugLog } from "@/lib/authDebugLog";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useRef, useState } from "react";

type LoginInfoModalProps =
  | {
      variant: "authenticated";
      companyIdFallback?: string | null;
    }
  | {
      variant: "guest";
    };

function ProfileField({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-gray-700/80 py-2.5 last:border-b-0">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label}</p>
      <p className="mt-0.5 text-sm text-gray-100">{value}</p>
    </div>
  );
}

type LoadState =
  | { kind: "loading" }
  | { kind: "ready"; profile: UserProfileDisplay; user: SessionUser }
  | { kind: "error"; message: string };

type MeFetchFailure = Error & { httpStatus: number };

async function fetchMeProfile(
  companyIdFallback: string | null | undefined,
  signal?: AbortSignal,
): Promise<{ profile: UserProfileDisplay; user: SessionUser }> {
  authDebugLog("fetchMeProfile → GET /api/me");
  const r = await fetch("/api/me", {
    signal,
    cache: "no-store",
    credentials: "same-origin",
  });
  const data = (await r.json()) as {
    user?: SessionUser;
    error?: string;
  };
  const raw = data.user;
  const uid = raw?.userId;
  if (!r.ok || uid === undefined || uid === null || String(uid).trim() === "" || !raw) {
    const err = new Error(data.error ?? `Failed to load profile (${r.status})`) as MeFetchFailure;
    err.httpStatus = r.status;
    throw err;
  }
  const user: SessionUser = { ...raw, userId: String(raw.userId) };
  return {
    profile: sessionUserToProfile(user, companyIdFallback),
    user,
  };
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
      aria-hidden
    >
      <path d="M9 6l6 6-6 6" />
    </svg>
  );
}

function ExternalLinkIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      className={className}
      aria-hidden
    >
      <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" />
    </svg>
  );
}

function InitialsAvatar({
  initials,
  size = "md",
  className = "",
}: {
  initials: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}) {
  const dim = size === "lg" ? "h-14 w-14 text-lg" : size === "sm" ? "h-8 w-8 text-xs" : "h-9 w-9 text-sm";
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full bg-amber-100 font-semibold text-gray-900 ${dim} ${className}`}
    >
      {initials}
    </span>
  );
}

function useCloseOnEscapeAndOutside(
  open: boolean,
  onClose: () => void,
  containerRef: React.RefObject<HTMLElement | null>,
) {
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    const onMouseDown = (e: MouseEvent) => {
      const root = containerRef.current;
      if (root && !root.contains(e.target as Node)) onCloseRef.current();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onMouseDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onMouseDown);
    };
  }, [open, containerRef]);
}

export function LoginInfoModal(props: LoginInfoModalProps) {
  const router = useRouter();
  const redirectToAuth0 = useCallback(() => {
    const url = new URL("/auth/login", window.location.origin);
    url.searchParams.set("returnTo", "/");
    window.location.assign(url.toString());
  }, []);

  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  useCloseOnEscapeAndOutside(menuOpen, closeMenu, menuRef);

  const [showDetails, setShowDetails] = useState(false);

  const [load, setLoad] = useState<LoadState>({ kind: "loading" });

  const companyIdFallback = props.variant === "authenticated" ? props.companyIdFallback : undefined;
  const companyIdRef = useRef(companyIdFallback);
  useEffect(() => {
    companyIdRef.current = companyIdFallback;
  }, [companyIdFallback]);

  const loadProfile = useCallback(
    async (signal?: AbortSignal, options?: { silent?: boolean }) => {
      authDebugLog("loadProfile", { silent: Boolean(options?.silent) });
      if (!options?.silent) {
        setLoad({ kind: "loading" });
      }
      try {
        const { profile, user } = await fetchMeProfile(companyIdRef.current, signal);
        setLoad({ kind: "ready", profile, user });
      } catch (err: unknown) {
        if (err instanceof Error && err.name === "AbortError") return;
        const message = err instanceof Error ? err.message : "Could not load session";
        setLoad({ kind: "error", message });
        const status =
          typeof err === "object" && err !== null && "httpStatus" in err
            ? (err as MeFetchFailure).httpStatus
            : undefined;
        if (status === 401) {
          authDebugLog("loadProfile failed with 401 → router.refresh()");
          router.refresh();
        }
      }
    },
    [router],
  );

  /** Light check on focus / visibility — clears stale "ready" if cookies were removed (e.g. DevTools). */
  const verifyStillSignedIn = useCallback(async () => {
    if (props.variant !== "authenticated") return;
    if (load.kind !== "ready") return;
    authDebugLog("verifyStillSignedIn (focus/visibility ping) → GET /api/me");
    try {
      const r = await fetch("/api/me", { credentials: "same-origin", cache: "no-store" });
      if (r.status === 401) {
        authDebugLog("verifyStillSignedIn: /api/me 401 → router.refresh()");
        router.refresh();
        setLoad({ kind: "error", message: "unauthenticated" });
        return;
      }
      if (!r.ok) {
        const data = (await r.json()) as { error?: string };
        setLoad({ kind: "error", message: data.error ?? `Session check failed (${r.status})` });
        authDebugLog("verifyStillSignedIn: /api/me not ok → router.refresh()", r.status);
        router.refresh();
      }
    } catch {
      /* ignore transient network errors on visibility ping */
    }
  }, [load.kind, props.variant, router]);

  const lastSessionPingRef = useRef(0);
  const scheduleSessionPing = useCallback(() => {
    if (props.variant !== "authenticated") return;
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastSessionPingRef.current < 1500) return;
    lastSessionPingRef.current = now;
    authDebugLog("scheduleSessionPing → verifyStillSignedIn");
    void verifyStillSignedIn();
  }, [props.variant, verifyStillSignedIn]);

  useEffect(() => {
    if (props.variant !== "authenticated") return;
    authDebugLog("LoginInfoModal mounted / variant authenticated → initial loadProfile");
    const ac = new AbortController();
    queueMicrotask(() => {
      void loadProfile(ac.signal);
    });
    return () => ac.abort();
  }, [props.variant, loadProfile]);

  useEffect(() => {
    if (props.variant !== "authenticated") return;
    document.addEventListener("visibilitychange", scheduleSessionPing);
    window.addEventListener("focus", scheduleSessionPing);
    return () => {
      document.removeEventListener("visibilitychange", scheduleSessionPing);
      window.removeEventListener("focus", scheduleSessionPing);
    };
  }, [props.variant, scheduleSessionPing]);

  const triggerGuestClass =
    "rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:border-gray-300 hover:bg-gray-100";

  if (props.variant === "guest") {
    return (
      <button type="button" onClick={redirectToAuth0} className={triggerGuestClass}>
        Login
      </button>
    );
  }

  if (load.kind === "loading") {
    return (
      <div
        className="flex h-9 w-9 shrink-0 animate-pulse items-center justify-center rounded-full bg-gray-200"
        aria-busy="true"
        aria-label="Loading account"
      />
    );
  }

  if (load.kind === "error") {
    return (
      <button type="button" onClick={redirectToAuth0} className={triggerGuestClass}>
        Login
      </button>
    );
  }

  const { profile, user } = load;
  const initials = getUserInitials(profile);
  const displayName = getFullDisplayName(profile);
  const companyTitle =
    profile.companySummaryName !== "—" ? profile.companySummaryName : "Longbridge Financial";

  return (
    <div ref={menuRef} className="relative">
      <button
        type="button"
        onClick={() => {
          setMenuOpen((open) => {
            if (!open) {
              authDebugLog("account menu opening → loadProfile(silent)");
              void loadProfile(undefined, { silent: true });
            }
            return !open;
          });
        }}
        className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-200 bg-amber-100 text-sm font-semibold text-gray-900 shadow-sm transition hover:border-gray-300 hover:bg-amber-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
        aria-label={`Account menu for ${displayName}`}
        aria-expanded={menuOpen}
        aria-haspopup="true"
      >
        <span aria-hidden>{initials}</span>
        <span
          className="absolute -bottom-0.5 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full border-2 border-white bg-green-500"
          aria-hidden
          title="Signed in"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
            className="h-2 w-2"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
        </span>
      </button>
      {menuOpen ? (
        <div className="absolute right-0 top-full z-[60] mt-2 w-[min(22rem,calc(100vw-2rem))] rounded border border-gray-700 bg-gray-900 p-0 text-white shadow-2xl">
          <div className="flex items-start justify-between gap-3 border-b border-gray-700 px-4 py-3">
            <span className="text-sm font-semibold text-white">{companyTitle}</span>
            <button
              type="button"
              className="shrink-0 cursor-pointer border-0 bg-transparent p-0 text-sm text-gray-400 transition hover:text-white"
              onClick={() => {
                authDebugLog("Sign out clicked → window.location.assign /auth/logout?returnTo=<origin>/", {
                  stack: new Error("stack").stack,
                });
                closeMenu();
                /* Avoid <a href> — browsers / Next may speculatively prefetch GET /auth/logout and clear cookies. */
                const logoutUrl = new URL("/auth/logout", window.location.origin);
                logoutUrl.searchParams.set("returnTo", `${window.location.origin}/`);
                window.location.assign(logoutUrl.toString());
              }}
            >
              Sign out
            </button>
          </div>

          <div className="flex gap-3 border-b border-gray-700 px-4 py-4">
            <InitialsAvatar initials={initials} size="lg" />
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-white" title={displayName}>
                {displayName}
              </p>
              <p className="truncate text-sm text-gray-400" title={profile.email}>
                {profile.email}
              </p>
              <button
                type="button"
                onClick={() => setShowDetails((s) => !s)}
                className="mt-2 inline-flex items-center gap-1 text-sm text-blue-400 transition hover:text-blue-300"
              >
                <ExternalLinkIcon className="h-3.5 w-3.5" />
                {showDetails ? "Hide account details" : "View account"}
              </button>
            </div>
          </div>

          {showDetails ? (
            <div className="border-b border-gray-700 px-4 py-3">
              <ProfileField label="User ID" value={user.userId} />
              {user.companyId ? (
                <ProfileField label="Company ID" value={user.companyId} />
              ) : null}
              <ProfileField label="First name" value={profile.firstName} />
              <ProfileField label="Last name" value={profile.lastName} />
              {profile.fullName !== "—" &&
              (profile.firstName === "—" || profile.lastName === "—") ? (
                <ProfileField label="Full name" value={profile.fullName} />
              ) : null}
              <ProfileField label="Company" value={profile.company} />
              <ProfileField label="NMLS #" value={profile.nmlsId} />
              <ProfileField label="Email" value={profile.email} />
              {user.roles && user.roles.length > 0 ? (
                <div className="pt-2">
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Roles</p>
                  <p className="mt-1 text-sm text-gray-200">{user.roles.join(", ")}</p>
                </div>
              ) : null}
            </div>
          ) : null}

          <ul className="py-1" role="list">
            <li>
              <button
                type="button"
                className="flex w-full items-center gap-2 border border-transparent px-4 py-2.5 text-left text-sm text-gray-200 hover:bg-gray-800"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-green-600/30 text-green-400">
                  ✓
                </span>
                <span className="flex-1">Available</span>
                <ChevronRightIcon className="h-4 w-4 shrink-0 text-gray-500" />
              </button>
            </li>
          </ul>
        </div>
      ) : null}
    </div>
  );
}
